<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { getAlmInfo } from "@/backend/read/BackendReader";
import InfoMessage from "@/components/ui/InfoMessage/InfoMessage.vue";
import type { BoardCard } from "@/model/card";

import BulkActionModal from "./BulkActionModal.vue";
import BlockedGroup from "./internal/BlockedGroup.vue";
import BulkActionGroup from "./internal/BulkActionGroup.vue";
import { useBulkSubtitle } from "./useEligibility";

const props = defineProps<{
  deletable: BoardCard[];
  blockedAlm: BoardCard[];
  blockedAlmReason: "config" | "multi";
  blockedMirror: BoardCard[];
}>();
const emit = defineEmits<{ delete: [] }>();

const { t } = useI18n();
const almInfo = computed(() => getAlmInfo());

const totalCount = computed(
  () => props.deletable.length + props.blockedAlm.length + props.blockedMirror.length,
);
const deletableCount = computed(() => props.deletable.length);
const subtitle = useBulkSubtitle(
  totalCount,
  deletableCount,
  /*$t*/ "action.bulkDelete.subtitlePartial",
);
const almDeletableId = computed(() => props.deletable.find((c) => !!c.data.almId)?.data.almId);

const blockedAlmTitle = computed(() =>
  props.blockedAlmReason === "multi"
    ? t(/*$t*/ "action.bulkDelete.blockedAlmMulti.title")
    : t(/*$t*/ "action.bulkDelete.blockedAlmConfig.title"),
);
const blockedAlmDescription = computed(() =>
  props.blockedAlmReason === "multi"
    ? t(/*$t*/ "action.bulkDelete.blockedAlmMulti.description", { almName: almInfo.value.name })
    : t(/*$t*/ "action.bulkDelete.blockedAlmConfig.description", { almName: almInfo.value.name }),
);

const anyBlockedStickies = computed(
  () => props.blockedAlm.length > 0 || props.blockedMirror.length > 0,
);
</script>

<template>
  <BulkActionModal
    :title="$t('action.bulkDelete.title')"
    :subtitle="subtitle"
    :primary-label="$t('action.bulkDelete.primary', { count: deletable.length })"
    primary-icon="general/delete"
    destructive
    :primary-disabled="deletable.length === 0"
    @primary="emit('delete')"
  >
    <BulkActionGroup :heading="$t('action.bulkDelete.willBeDeletedHeading')" :cards="deletable">
      <InfoMessage v-if="almDeletableId" level="warn" size="small" :icon="almInfo.colorIcon">
        {{
          $t("action.deleteAlmSticky.willAlsoBeDeleted", {
            almId: almDeletableId,
            almName: almInfo.name,
          })
        }}
      </InfoMessage>
    </BulkActionGroup>

    <template v-if="anyBlockedStickies" #blocked>
      <BlockedGroup
        :title="blockedAlmTitle"
        :description="blockedAlmDescription"
        :cards="blockedAlm"
      />
      <BlockedGroup
        :title="$t('action.bulkDelete.blockedMirror.title')"
        :description="$t('action.bulkDelete.blockedMirror.description')"
        :cards="blockedMirror"
      />
    </template>
  </BulkActionModal>
</template>
